import 'zod/compile';
import { z } from 'zod';
import type { DateTimeRange } from '@/components/common/date-pickers/date-time-range-picker';
import { downloadedRangeBounds } from './downloadedRange';
import {
  DEFAULT_VENDOR_PORTAL_FILES_PAGE_SIZE,
  VENDOR_PORTAL_FILES_PAGE_SIZES,
  type VendorPortalFileFilters,
} from './useVendorPortalFiles';

/**
 * The Vendor Portals page's filters and page, kept in the URL so a link or a reload shows
 * exactly what the user was looking at. A value the page cannot read is dropped rather
 * than failing the page.
 */
export const VendorPortalsSearchSchema = z.object({
  vendor: z.string().min(1).optional().catch(undefined),
  /** Vendor Account number or name, as typed. */
  account: z.string().min(1).optional().catch(undefined),
  fileType: z.string().min(1).optional().catch(undefined),
  /** The first instant of the Downloaded range. */
  downloadedFrom: z.iso.datetime().optional().catch(undefined),
  /** The last second of the Downloaded range, inclusive. */
  downloadedTo: z.iso.datetime().optional().catch(undefined),
  /** Only one workflow run's files. */
  run: z.uuid().optional().catch(undefined),
  /** 1-based. */
  page: z.coerce.number().int().min(1).optional().catch(undefined),
  pageSize: z.coerce
    .number()
    .refine((size) =>
      (VENDOR_PORTAL_FILES_PAGE_SIZES as readonly number[]).includes(size),
    )
    .optional()
    .catch(undefined),
});

export type VendorPortalsSearch = z.infer<typeof VendorPortalsSearchSchema>;

/** The Downloaded range the URL names, for the picker. */
export function downloadedRangeFromSearch(
  search: VendorPortalsSearch,
): DateTimeRange | undefined {
  if (!search.downloadedFrom) return undefined;
  return {
    from: new Date(search.downloadedFrom),
    to: search.downloadedTo ? new Date(search.downloadedTo) : undefined,
  };
}

/** The filters the list and the zip download share. */
export function filtersFromSearch(
  search: VendorPortalsSearch,
): VendorPortalFileFilters {
  return {
    vendor: search.vendor,
    account: search.account?.trim() || undefined,
    fileType: search.fileType,
    ...downloadedRangeBounds(downloadedRangeFromSearch(search)),
    workflowRunId: search.run,
  };
}

/** The page of the list the URL names. */
export function pageFromSearch(search: VendorPortalsSearch): {
  limit: number;
  offset: number;
} {
  const limit = search.pageSize ?? DEFAULT_VENDOR_PORTAL_FILES_PAGE_SIZE;
  return { limit, offset: ((search.page ?? 1) - 1) * limit };
}
